
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { ProjectDetailsDialog } from "./ProjectDetailsDialog";
import { Button } from "../ui/button";
import { Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ConfirmDialog } from "../ui/confirm-dialog";
import { useOrganization } from "@/contexts/OrganizationContext";

export const ProjectList = () => {
  const queryClient = useQueryClient();
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();

  const { data, isLoading, error } = useQuery({
    queryKey: ["projects", currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization) throw new Error("No organization selected");
      
      // Direct database query with relationships - much faster than edge function
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          project_contacts (
            contacts (
              id,
              name,
              email,
              company
            )
          ),
          project_milestones (
            id,
            title,
            completed,
            due_date
          )
        `)
        .eq('organization_id', currentOrganization.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentOrganization,
  });

  useEffect(() => {
    if (!currentOrganization) return;
    
    // Set up the Supabase real-time subscription for projects
    const channel = supabase
      .channel('schema-db-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'projects',
          filter: `organization_id=eq.${currentOrganization.id}`
        },
        (payload) => {
          console.log("Received real-time update for projects:", payload);
          queryClient.invalidateQueries({ queryKey: ["projects", currentOrganization.id] });
          
          // If currently viewing a project that was updated, refresh that project's details
          if (selectedProject && 
              payload.new && 
              'id' in selectedProject && 
              'id' in payload.new && 
              selectedProject.id === payload.new.id) {
            const updatedProject = payload.new;
            setSelectedProject(prevProject => ({
              ...prevProject,
              ...updatedProject
            }));
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_contacts',
        },
        (payload) => {
          console.log("Received real-time update for project_contacts:", payload);
          queryClient.invalidateQueries({ queryKey: ["projects", currentOrganization.id] });
          
          // If we receive an update on project_contacts, we should refresh the project details
          if (selectedProject && 'id' in selectedProject && isDetailsOpen) {
            const projectId = selectedProject.id;
            fetchProjectDetails(projectId);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_milestones',
        },
        (payload) => {
          console.log("Received real-time update for project_milestones:", payload);
          queryClient.invalidateQueries({ queryKey: ["projects", currentOrganization.id] });
          
          // If we receive an update on project_milestones, refresh the project details
          if (selectedProject && 'id' in selectedProject && isDetailsOpen) {
            const projectId = selectedProject.id;
            fetchProjectDetails(projectId);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient, currentOrganization, selectedProject, isDetailsOpen]);

  // Function to fetch updated project details when needed
  const fetchProjectDetails = async (projectId: string) => {
    try {
      if (!currentOrganization) return;
      
      // Direct database query for project details refresh
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          project_contacts (
            contacts (
              id,
              name,
              email,
              company
            )
          ),
          project_milestones (
            id,
            title,
            completed,
            due_date
          )
        `)
        .eq('organization_id', currentOrganization.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      const updatedProjects = data || [];
      const updatedProject = updatedProjects.find((p: any) => p.id === projectId);
      
      if (updatedProject) {
        setSelectedProject(updatedProject);
      }
    } catch (error) {
      console.error('Error fetching project details:', error);
    }
  };

  const handleDelete = async () => {
    if (!projectToDelete?.id || isDeleting || !currentOrganization) return;

    try {
      setIsDeleting(true);
      
      const { error } = await supabase.functions.invoke('project-operations', {
        body: {
          action: "delete",
          projectId: projectToDelete.id,
          organizationId: currentOrganization.id
        }
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Project deleted successfully",
      });
      
      queryClient.invalidateQueries({ queryKey: ["projects", currentOrganization.id] });
    } catch (error) {
      console.error('Error deleting project:', error);
      toast({
        title: "Error",
        description: "Failed to delete project",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setProjectToDelete(null);
    }
  };

  if (!currentOrganization) {
    return <div className="text-center p-4">Please select an organization to view projects.</div>;
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-500">Error loading projects. Please try again later.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="mt-2">Loading projects...</p>
      </div>
    );
  }

  const getProgressPercentage = (milestones: any[]) => {
    if (!milestones?.length) return 0;
    const completed = milestones.filter(m => m.completed).length;
    return Math.round((completed / milestones.length) * 100);
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-green-500';
      case 'in progress': return 'bg-blue-500';
      case 'on hold': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const handleRowClick = (project: any) => {
    setSelectedProject(project);
    setIsDetailsOpen(true);
  };

  const projects = data || [];

  return (
    <div className="space-y-6">
      {projects.length > 0 ? (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Timeline</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Clients</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {projects.map((project) => (
                <TableRow 
                  key={project.id}
                  className="cursor-pointer hover:bg-muted/50"
                >
                  <TableCell 
                    className="font-medium"
                    onClick={() => handleRowClick(project)}
                  >
                    {project.name}
                  </TableCell>
                  <TableCell onClick={() => handleRowClick(project)}>
                    <Badge className={getStatusColor(project.status)}>
                      {project.status || 'Not Set'}
                    </Badge>
                  </TableCell>
                  <TableCell onClick={() => handleRowClick(project)}>
                    {project.start_date && (
                      <span>
                        {format(new Date(project.start_date), 'MMM d, yyyy')}
                        {project.end_date && ' - '}
                        {project.end_date && format(new Date(project.end_date), 'MMM d, yyyy')}
                      </span>
                    )}
                  </TableCell>
                  <TableCell onClick={() => handleRowClick(project)}>
                    <div className="w-[100px]">
                      <Progress value={getProgressPercentage(project.project_milestones)} />
                    </div>
                  </TableCell>
                  <TableCell onClick={() => handleRowClick(project)}>
                    {project.project_contacts?.map((pc: any) => (
                      <Badge key={pc.contacts.id} variant="outline" className="mr-1">
                        {pc.contacts.name}
                      </Badge>
                    ))}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setProjectToDelete(project);
                      }}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="text-center p-4">
          <p className="text-muted-foreground">No projects found</p>
        </div>
      )}

      <ProjectDetailsDialog
        project={selectedProject}
        open={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
        onProjectUpdate={(updatedProject) => {
          setSelectedProject(updatedProject);
        }}
      />

      <ConfirmDialog
        open={projectToDelete !== null}
        onOpenChange={(open) => !open && setProjectToDelete(null)}
        onConfirm={handleDelete}
        title="Delete Project"
        description={`Are you sure you want to delete ${projectToDelete?.name}? This action cannot be undone and will remove all associated data.`}
      />
    </div>
  );
};

// Helper function for progress calculation
const getProgressPercentage = (milestones: any[]) => {
  if (!milestones?.length) return 0;
  const completed = milestones.filter(m => m.completed).length;
  return Math.round((completed / milestones.length) * 100);
};

// Helper function for status color
const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'completed': return 'bg-green-500';
    case 'in progress': return 'bg-blue-500';
    case 'on hold': return 'bg-yellow-500';
    default: return 'bg-gray-500';
  }
};
